import type { ThemeVariant } from "@opensea/ui-kit"
import { ThemeProvider as OSThemeProvider } from "@opensea/ui-kit"
import {
  ThemeProvider as NextThemesProvider,
  useTheme as useNextTheme,
} from "next-themes"
import type React from "react"
import { useCallback } from "react"

export type Theme = ThemeVariant | "system"

type Props = {
  children: React.ReactNode
  theme?: ThemeVariant
  nonce?: string
}

export const ThemeProvider = ({ children, theme, nonce }: Props) => {
  return (
    <NextThemesProvider attribute="class" defaultTheme={theme} nonce={nonce}>
      <InnerThemeProvider>{children}</InnerThemeProvider>
    </NextThemesProvider>
  )
}

const InnerThemeProvider = ({ children }: Props) => {
  const { resolvedTheme } = useTheme()
  return <OSThemeProvider theme={resolvedTheme}>{children}</OSThemeProvider>
}

export const useTheme = () => {
  const {
    resolvedTheme: untypedResolvedTheme,
    setTheme,
    theme: untypedTheme,
  } = useNextTheme()
  const resolvedTheme = untypedResolvedTheme as ThemeVariant | undefined
  const theme = untypedTheme as Theme | undefined

  const handleSetTheme = useCallback(
    (theme: Theme) => {
      if (!document.startViewTransition) {
        setTheme(theme)
        return
      }

      document.startViewTransition(() => {
        setTheme(theme)
      })
    },
    [setTheme],
  )

  const toggleTheme = useCallback(() => {
    const nextTheme: ThemeVariant = resolvedTheme === "dark" ? "light" : "dark"

    handleSetTheme(nextTheme)

    return nextTheme
  }, [resolvedTheme, handleSetTheme])

  return {
    theme,
    resolvedTheme,
    toggleTheme,
    setTheme: handleSetTheme as (theme: Theme) => unknown,
  }
}
